import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import Calendar from './components/Calendar';
import DayModal from './components/DayModal';
import CustomerDirectory from './components/CustomerDirectory';
import WeeklySummary from './components/WeeklySummary';

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const expandRecurringAppointments = (appointments, startDate, endDate) => {
  const expanded = { ...appointments };

  const getNextRecurringDate = (date, pattern) => {
    const next = new Date(date);
    if (pattern === 'weekly') {
      next.setDate(next.getDate() + 7);
      return next;
    }
    if (pattern === 'biweekly') {
      next.setDate(next.getDate() + 14);
      return next;
    }

    const day = next.getDate();
    next.setMonth(next.getMonth() + 1);
    if (next.getDate() !== day) {
      return null;
    }
    return next;
  };

  Object.keys(appointments).forEach((dateKey) => {
    const dayCust = appointments[dateKey];
    if (!Array.isArray(dayCust)) return;

    dayCust.forEach((customer) => {
      if (!customer.isRecurring) return;

      const [year, month, day] = dateKey.split('-').map(Number);
      const originalDate = new Date(year, month - 1, day);
      let currentDate = getNextRecurringDate(originalDate, customer.recurringPattern);
      const endRecurDate = customer.recurringEndDate ? new Date(customer.recurringEndDate) : null;

      while (currentDate && currentDate <= endDate) {
        if ((!endRecurDate || currentDate <= endRecurDate) && currentDate >= startDate) {
          const currentDateKey = formatDateKey(currentDate);
          if (currentDateKey !== dateKey) {
            if (!expanded[currentDateKey]) {
              expanded[currentDateKey] = [];
            }
            expanded[currentDateKey].push(customer);
          }
        }

        currentDate = getNextRecurringDate(currentDate, customer.recurringPattern);
      }
    });
  });

  return expanded;
};

function App() {
  const [view, setView] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(formatDateKey(new Date()));
  const [appointments, setAppointments] = useState({});
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [hasLoadedAppointments, setHasLoadedAppointments] = useState(false);

  const getUserKey = (user) => {
    if (!user) return null;
    if (user.email) return user.email.toLowerCase();
    return user.id;
  };

  useEffect(() => {
    const handleInit = (currentUser) => {
      console.log('Netlify Identity init:', currentUser);
      setUser(currentUser);
      setAuthReady(true);
    };

    const handleLogin = (user) => {
      console.log('User logged in:', user);
      setUser(user);
    };

    const handleLogout = () => {
      console.log('User logged out');
      setUser(null);
    };

    window.netlifyIdentity.on('init', handleInit);
    window.netlifyIdentity.on('login', handleLogin);
    window.netlifyIdentity.on('logout', handleLogout);
    window.netlifyIdentity.init();

    const currentUser = window.netlifyIdentity.currentUser();
    if (currentUser) {
      setUser(currentUser);
      setAuthReady(true);
    }

    return () => {
      if (window.netlifyIdentity.off) {
        window.netlifyIdentity.off('init', handleInit);
        window.netlifyIdentity.off('login', handleLogin);
        window.netlifyIdentity.off('logout', handleLogout);
      }
    };
  }, []);

  useEffect(() => {
    if (!authReady) {
      return;
    }

    if (user) {
      const loadData = async () => {
        try {
          const userKey = getUserKey(user);
          console.log('Loading data for user key:', userKey);
          let docRef = doc(db, 'appointments', userKey);
          let docSnap = await getDoc(docRef);

          if (!docSnap.exists() && user.id && user.email) {
            const fallbackRef = doc(db, 'appointments', user.id);
            const fallbackSnap = await getDoc(fallbackRef);
            if (fallbackSnap.exists()) {
              console.log('Loaded fallback data for user id:', user.id);
              setAppointments(fallbackSnap.data());
              setHasLoadedAppointments(true);
              return;
            }
          }

          if (docSnap.exists()) {
            console.log('Data loaded:', docSnap.data());
            setAppointments(docSnap.data());
          } else {
            console.log('No data found for user');
            setAppointments({});
          }
          setHasLoadedAppointments(true);
        } catch (error) {
          console.error('Error loading data:', error);
        }
      };
      loadData();
    } else {
      console.log('No user, clearing appointments');
      setAppointments({});
      setHasLoadedAppointments(false);
    }
  }, [authReady, user]);

  useEffect(() => {
    if (!authReady || !hasLoadedAppointments || !user) {
      return;
    }

    const saveData = async () => {
      try {
        const userKey = getUserKey(user);
        console.log('Saving data for user key:', userKey, appointments);
        await setDoc(doc(db, 'appointments', userKey), appointments);
        console.log('Data saved successfully');
      } catch (error) {
        console.error('Error saving data:', error);
      }
    };
    saveData();
  }, [appointments, user, authReady, hasLoadedAppointments]);

  const monthLabel = useMemo(() => {
    return currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [currentMonth]);

  const expandedAppointments = useMemo(() => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    return expandRecurringAppointments(appointments, firstDay, lastDay);
  }, [appointments, currentMonth]);

  const selectedCustomerList = expandedAppointments[selectedDate] || [];

  const handleAddAppointment = (customer) => {
    setAppointments((prev) => {
      const dayList = prev[selectedDate] || [];
      return {
        ...prev,
        [selectedDate]: [...dayList, customer],
      };
    });
  };

  const handleUpdateAppointment = (index, updatedCustomer) => {
    setAppointments((prev) => {
      const dayList = [...(prev[selectedDate] || [])];
      dayList[index] = updatedCustomer;
      return {
        ...prev,
        [selectedDate]: dayList,
      };
    });
  };

  const handleDeleteAppointment = (dateKey, index) => {
    setAppointments((prev) => {
      const dayList = [...(prev[dateKey] || [])];
      dayList.splice(index, 1);
      const next = { ...prev };
      if (dayList.length > 0) {
        next[dateKey] = dayList;
      } else {
        delete next[dateKey];
      }
      return next;
    });
  };

  const handleChangeMonth = (offset) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + offset, 1);
      return next;
    });
  };

  const handleLogin = () => {
    window.netlifyIdentity.open();
  };

  const handleLogout = () => {
    window.netlifyIdentity.logout();
  };

  if (!authReady) {
    return (
      <div className="login-screen">
        <h1>Cleaning Business Manager</h1>
        <p>Loading authentication…</p>
      </div>
    );
  }

  if (!user) {
    console.log('Rendering login screen');
    return (
      <div className="login-screen">
        <h1>Cleaning Business Manager</h1>
        <p>Please log in to access your appointments.</p>
        <button onClick={handleLogin}>Log In</button>
      </div>
    );
  }

  console.log('Rendering app for user:', user.id);
  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <p className="eyebrow">Cleaning Business Manager</p>
          <h1>Schedule and customer details</h1>
          <p>Click a day to add or review cleaning appointments.</p>
        </div>
        <button onClick={handleLogout}>Log Out</button>
      </header>

      <div className="view-switcher">
        <button
          className={view === 'calendar' ? 'active' : ''}
          onClick={() => setView('calendar')}
          type="button"
        >
          Schedule
        </button>
        <button
          className={view === 'customers' ? 'active' : ''}
          onClick={() => setView('customers')}
          type="button"
        >
          Customer directory
        </button>
        <button
          className={view === 'summary' ? 'active' : ''}
          onClick={() => setView('summary')}
          type="button"
        >
          Weekly summary
        </button>
      </div>

      <main className="app-content">
        <section className="calendar-pane">
          <div className="month-header">
            <button onClick={() => handleChangeMonth(-1)} aria-label="Previous month">←</button>
            <div>{monthLabel}</div>
            <button onClick={() => handleChangeMonth(1)} aria-label="Next month">→</button>
          </div>
          <Calendar
            monthDate={currentMonth}
            events={expandedAppointments}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
        </section>

        <section className="details-pane">
          {view === 'calendar' ? (
            <DayModal
              dateKey={selectedDate}
              customers={selectedCustomerList}
              onAddCustomer={handleAddAppointment}
              onUpdateCustomer={handleUpdateAppointment}
              onDeleteCustomer={(index) => handleDeleteAppointment(selectedDate, index)}
            />
          ) : view === 'customers' ? (
            <CustomerDirectory
              appointments={expandedAppointments}
              onDeleteAppointment={handleDeleteAppointment}
              onSelectDate={(dateKey) => {
                setSelectedDate(dateKey);
                setView('calendar');
              }}
            />
          ) : (
            <WeeklySummary appointments={expandedAppointments} />
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
